import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
    const tok = localStorage.getItem("analyzio_token");
    if (tok) config.headers.Authorization = `Bearer ${tok}`;
    
    // Inject the user's explicitly pinned target workspace (if they have one)
    if (typeof window !== "undefined") {
        try {
            const pinnedWsId = localStorage.getItem("analyzio_pinned_workspace_id");
            if (pinnedWsId) {
                config.headers["X-Workspace-Id"] = pinnedWsId;
            }
        } catch {}
    }
    
    return config;
});

// --- Response envelope auto-unwrap ---
// Backend wraps every successful JSON response in
//   { success: true, data: <payload>, request_id: "..." }
// We unwrap transparently so existing `response.data.foo` access keeps
// working without changes to every call site. Error responses keep their
// shape (handled by the error branch below + existing code).
function unwrapEnvelope(response) {
    const body = response?.data;
    if (body && typeof body === "object" && body.success === true && "data" in body) {
        // Preserve request_id on the axios response for debugging/logging
        response.requestId = body.request_id;
        response.data = body.data;
    }
    return response;
}

api.interceptors.response.use(
    (r) => unwrapEnvelope(r),
    (err) => {
        // Propagate request_id to axios error object for easier logging.
        const rid = err?.response?.data?.request_id;
        if (rid) err.requestId = rid;

        // iter-120 B4 — Plan-limit 402 → broadcast a window event so the
        // global UpsellToast listener (mounted in AppShell) renders the
        // structured upsell. The original error still propagates so the
        // caller sees the failure and can fall back to its own handler.
        if (err.response && err.response.status === 402) {
            const payload = err.response.data;
            if (payload && payload.error === "plan_limit_exceeded") {
                try {
                    window.dispatchEvent(new CustomEvent("plan-limit-exceeded", { detail: payload }));
                } catch {}
            }
            // Sense AI credit exhaustion → broadcast so the global upsell
            // (AiCreditsUpsell in AppShell) can offer an inline top-up.
            const aiDetail = payload?.detail;
            if (aiDetail && aiDetail.error === "ai_credits_exhausted") {
                try {
                    window.dispatchEvent(new CustomEvent("ai-credits-exhausted", { detail: aiDetail }));
                } catch {}
            }
        }

        // iter-2026 — Trial-expired 403 hard gate. Protected app routes return
        // 403 {detail:{code:"trial_expired"}} once the 14-day trial ends. We
        // broadcast a window event so the auth context can refresh /auth/me and
        // the global TrialBanner overlay appears immediately, and we tag the
        // error as handled so background data fetches fail quietly behind the
        // overlay instead of surfacing noisy uncaught rejections.
        if (err.response && err.response.status === 403) {
            const detail = err.response?.data?.detail;
            const code = typeof detail === "object" ? detail?.code : undefined;
            if (code === "trial_expired") {
                err.trialExpired = true;
                try {
                    window.dispatchEvent(new CustomEvent("trial-expired", { detail }));
                } catch {}
            }
        }

        if (err.response && err.response.status === 401) {
            const path = window.location.pathname;
            const isPublicAuthPath = [
                "/login",
                "/signup",
                "/reset-password",
                "/accept-invite",
                "/accept-workspace-invite",
                "/sso/callback",
            ].some((p) => path.startsWith(p));

            if (!isPublicAuthPath) {
                // Preserve an "you were signed out" message for the login screen
                const detail = err.response?.data?.detail;
                if (detail && typeof detail === "string" && detail.toLowerCase().includes("session")) {
                    sessionStorage.setItem("analyzio_logout_reason", detail);
                }
                localStorage.removeItem("analyzio_token");
                localStorage.removeItem("analyzio_user");
                window.location.href = "/login";
            }
        }
        return Promise.reject(err);
    },
);

export default api;
